import React, { useEffect, useState } from "react";
import axios from "axios";
import { Flag, TrendingUp, Megaphone, Vote } from "lucide-react";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso, lang) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(
      lang === "ml" ? "ml-IN" : "en-IN",
      { year: "numeric", month: "short", day: "numeric" },
    );
  } catch {
    return iso;
  }
}

function fmtCount(n, lang) {
  if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}${lang === "ml" ? " ലക്ഷം" : "L"}`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toLocaleString();
}

/**
 * Combined timeline:
 *   Launch → 50K → 100K → 300K → 500K → CM Announced
 *
 * Crossing timestamps for 50K/100K/300K/500K come from /api/milestones.
 * In dev with <50K votes the count nodes show "—" until production data
 * (510K+ votes) populates them.
 *
 * Layout: horizontal track on desktop, vertical stack on mobile.
 */
export const FinalTimeline = ({ pollState, results }) => {
  const { t, lang } = useLang();
  const [milestones, setMilestones] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/milestones`)
      .then((r) => {
        if (alive) setMilestones(r.data?.milestones || []);
      })
      .catch(() => {
        if (alive) setMilestones([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!pollState) return null;

  const launchedDate = fmtDate(pollState.launched_at, lang);
  const announcedDate = fmtDate(pollState.announced_at, lang);
  const totalVotes = results?.total_votes ?? 0;

  // Build the ordered node list
  const milestoneMap = (milestones || []).reduce((acc, m) => {
    acc[m.count] = m.timestamp;
    return acc;
  }, {});

  const nodes = [
    {
      key: "launch",
      icon: Flag,
      label: t("timeline_launched"),
      date: launchedDate,
      sub: "4:00 PM IST",
      color: "#138808",
      ink: "#FFFFFF",
      reached: true,
    },
    ...MILESTONE_TARGETS_FRONTEND.map((n) => ({
      key: `m-${n}`,
      icon: TrendingUp,
      label: `${fmtCount(n, lang)} ${t("votes_word")}`,
      date: fmtDate(milestoneMap[n], lang),
      sub: null,
      color: "#FF9933",
      ink: "#0A0A0A",
      reached: totalVotes >= n && !!milestoneMap[n],
    })),
  ];

  if (pollState.frozen) {
    nodes.push({
      key: "announced",
      icon: Megaphone,
      label: t("timeline_announced"),
      date: announcedDate,
      sub: null,
      color: "#E3000F",
      ink: "#FFFFFF",
      reached: true,
    });
  } else {
    nodes.push({
      key: "total",
      icon: Vote,
      label: t("timeline_total_votes"),
      date: totalVotes.toLocaleString(),
      sub: null,
      color: "#0A0A0A",
      ink: "#FF9933",
      reached: true,
    });
  }

  // Only render the section in frozen mode (live mode keeps it minimal — caller decides via parent gating).
  if (!pollState.frozen) return null;

  return (
    <section className="space-y-6 fade-up" data-testid="final-timeline">
      <h2
        className="font-display uppercase leading-none"
        style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
        data-testid="timeline-heading"
      >
        {t("timeline_heading")}
      </h2>

      {/* Horizontal track on md+, vertical on mobile */}
      <div
        className="brutal-card p-4 md:p-6 overflow-x-auto"
        data-testid="timeline-track"
      >
        <ol
          className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-2 md:min-w-max"
          role="list"
        >
          {nodes.map((node, idx) => {
            const Icon = node.icon;
            const isLast = idx === nodes.length - 1;
            return (
              <React.Fragment key={node.key}>
                <li
                  className="flex md:flex-col items-start md:items-center gap-3 md:gap-2 md:min-w-[150px] md:text-center"
                  data-testid={`timeline-node-${node.key}`}
                  data-reached={node.reached}
                  style={{ opacity: node.reached ? 1 : 0.4 }}
                >
                  <div
                    className="border-4 border-black flex items-center justify-center shrink-0"
                    style={{
                      background: node.color,
                      color: node.ink,
                      width: 56,
                      height: 56,
                    }}
                    aria-hidden="true"
                  >
                    <Icon size={24} strokeWidth={3} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs uppercase font-bold tracking-[0.25em] text-neutral-600 leading-tight">
                      {node.label}
                    </p>
                    <p
                      className="font-display uppercase leading-none mt-1"
                      style={{ fontSize: "1.1rem" }}
                    >
                      {node.date || "—"}
                    </p>
                    {node.sub && (
                      <p className="text-[10px] uppercase font-bold tracking-[0.2em] text-neutral-500 mt-1">
                        {node.sub}
                      </p>
                    )}
                  </div>
                </li>

                {!isLast && (
                  <li
                    className="hidden md:flex items-center shrink-0"
                    aria-hidden="true"
                  >
                    <div
                      className="h-1 w-8 border-y-2 border-black"
                      style={{ background: "#0A0A0A" }}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

const MILESTONE_TARGETS_FRONTEND = [50_000, 100_000, 300_000, 500_000];

export default FinalTimeline;
